import { SortUrlService } from '@agilox/common';
import { MatTableResizeColsDirective } from '@agilox/directive/mat-table-resize-cols';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { EmptyPipe } from '@agilox/pipe/empty';
import {
	EmptyStateComponent,
	HeadlineComponent,
	IconModule,
	InputModule,
	SpinnerComponent,
} from '@agilox/ui';
import { Component, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import { map, tap } from 'rxjs';
import { DetailComponent } from '../../detail/detail.component';
import { TableIcon } from '../../general/enums/table-icon.enum';
import { TableService } from '../../general/services/table.service';
import { IpcData } from '../../ipcData';
import { TESTS_DEFAULT_SORT, TESTS_SORTABLE_COLUMNS } from './tests-sort.const';

const UNSEARCHABLE_KEY: keyof IpcData = 'raw';

@Component({
	selector: 'selftest-table',
	templateUrl: './tests.component.html',
	imports: [
		HeadlineComponent,
		InputModule,
		FormField,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		IconModule,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		EmptyStateComponent,
		SpinnerComponent,
		MatPaginator,
		DetailComponent,
		TranslatePipe,
		EmptyPipe,
		CustomDatePipe,
		CustomTimePipe,
		MatTableResizeColsDirective,
	],
})
export class TestsComponent {
	private tableService = inject(TableService);
	private sortUrlService = inject(SortUrlService);

	matSort = viewChild<MatSort>(MatSort);
	paginator = viewChild<MatPaginator>(MatPaginator);

	sort = signal<Sort>(this.sortUrlService.read(TESTS_DEFAULT_SORT, TESTS_SORTABLE_COLUMNS));

	searchField = form(signal(''));

	displayedColumns = ['status', 'serialnumber', 'start', 'latest', 'runs', 'modemStatus', 'action'];
	expandedElement = signal<IpcData | undefined>(undefined);

	data = toSignal(
		this.tableService.fetchData().pipe(
			map((data: IpcData[]) => {
				const dataSource = new MatTableDataSource(data);
				dataSource.filterPredicate = (row: IpcData, filter: string) =>
					Object.entries(row).some(
						([key, value]) => key !== UNSEARCHABLE_KEY && `${value}`.toLowerCase().includes(filter)
					);
				return dataSource;
			}),
			tap((dataSource: MatTableDataSource<any>) => {
				// wait until the next tick to apply the paginator and sort
				setTimeout(() => {
					dataSource.paginator = this.paginator();
					dataSource.sort = this.matSort();
				});
			})
		)
	);

	constructor() {
		effect(() => {
			const search = this.searchField().value();
			const dataSource = this.data();
			if (dataSource) {
				untracked(() => {
					dataSource.filter = search.trim().toLowerCase();
				});
			}
		});
	}

	onSort(event: Sort) {
		this.sort.set(event);
		this.sortUrlService.write(event, TESTS_DEFAULT_SORT);
	}

	viewDetails(element: IpcData) {
		this.expandedElement.set(element);
	}

	closeDetails() {
		this.expandedElement.set(undefined);
	}

	protected readonly TableIcon = TableIcon;
}
